import { useMemo, useState } from 'react';

const W = 640, H = 220, PL = 48, PR = 12, PT = 12, PB = 26;

const niceMax = (v: number) => {
  if (v <= 4) return 4;
  const p = 10 ** Math.floor(Math.log10(v));
  return [1, 2, 2.5, 5, 10].map((m) => m * p).find((x) => x >= v)!;
};
export const shortNum = (v: number) => (Math.abs(v) >= 1e6 ? `${(v / 1e6).toFixed(1).replace(/\.0$/, '')}M` : Math.abs(v) >= 1e3 ? `${(v / 1e3).toFixed(1).replace(/\.0$/, '')}k` : `${Math.round(v * 100) / 100}`);
const dayLabel = (d: string) => new Date(`${d}T00:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });

/** Least-squares line through the points, for the trend. */
function trend(ys: number[]) {
  const n = ys.length;
  if (n < 2) return null;
  const mx = (n - 1) / 2, my = ys.reduce((a, b) => a + b, 0) / n;
  let num = 0, den = 0;
  ys.forEach((y, x) => { num += (x - mx) * (y - my); den += (x - mx) ** 2; });
  const slope = den ? num / den : 0;
  return { a: my - slope * mx, slope };
}

export interface Series { name: string; values: number[]; color: string; format?: (v: number) => string }

/** Line chart over days, with an optional dashed trend line for the first series. */
export function LineChart({ days, series, showTrend = true, area = true, label }: { days: string[]; series: Series[]; showTrend?: boolean; area?: boolean; label: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = niceMax(Math.max(0, ...series.flatMap((s) => s.values)));
  const x = (i: number) => PL + (days.length <= 1 ? 0 : (i / (days.length - 1)) * (W - PL - PR));
  const y = (v: number) => PT + (1 - v / max) * (H - PT - PB);
  const t = showTrend && series[0] ? trend(series[0].values) : null;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);
  const every = Math.max(1, Math.ceil(days.length / 6));
  const fmt = series[0]?.format ?? shortNum;
  return (
    <figure className="chart" aria-label={label}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label} onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => { const r = (e.currentTarget as SVGSVGElement).getBoundingClientRect(); const px = ((e.clientX - r.left) / r.width) * W; setHover(Math.max(0, Math.min(days.length - 1, Math.round(((px - PL) / (W - PL - PR)) * (days.length - 1))))); }}>
        {ticks.map((v) => <g key={v}><line x1={PL} x2={W - PR} y1={y(v)} y2={y(v)} className="ch-grid" /><text x={PL - 6} y={y(v) + 4} textAnchor="end" className="ch-ax">{fmt(v)}</text></g>)}
        {days.map((d, i) => (i % every === 0 || i === days.length - 1) && <text key={d} x={x(i)} y={H - 6} textAnchor={i === 0 ? 'start' : i === days.length - 1 ? 'end' : 'middle'} className="ch-ax">{dayLabel(d)}</text>)}
        {series.map((s, si) => {
          const pts = s.values.map((v, i) => `${x(i)},${y(v)}`).join(' ');
          return (
            <g key={s.name}>
              {area && si === 0 && <polygon points={`${x(0)},${y(0)} ${pts} ${x(s.values.length - 1)},${y(0)}`} fill={s.color} opacity=".09" />}
              <polyline points={pts} fill="none" stroke={s.color} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
            </g>
          );
        })}
        {t && <line x1={x(0)} y1={y(Math.max(0, t.a))} x2={x(days.length - 1)} y2={y(Math.max(0, t.a + t.slope * (days.length - 1)))} className="ch-trend" />}
        {hover !== null && <g>
          <line x1={x(hover)} x2={x(hover)} y1={PT} y2={H - PB} className="ch-cross" />
          {series.map((s) => <circle key={s.name} cx={x(hover)} cy={y(s.values[hover] ?? 0)} r="4" fill={s.color} stroke="var(--surface)" strokeWidth="2" />)}
        </g>}
      </svg>
      <figcaption className="ch-leg">
        {hover !== null ? <span className="ch-tip"><b>{dayLabel(days[hover]!)}</b>{series.map((s) => <span key={s.name}><i style={{ background: s.color }} />{s.name}: {(s.format ?? shortNum)(s.values[hover] ?? 0)}</span>)}</span>
          : <>{series.map((s) => <span key={s.name}><i style={{ background: s.color }} />{s.name}</span>)}{t && <span><i className="dash" />Trend {t.slope >= 0 ? 'up' : 'down'}</span>}</>}
      </figcaption>
    </figure>
  );
}

/** Horizontal bars, for comparing a handful of categories. */
export function BarChart({ rows, color = 'var(--amber)', format = shortNum, label }: { rows: { label: string; value: number; color?: string }[]; color?: string; format?: (v: number) => string; label: string }) {
  const max = useMemo(() => Math.max(1, ...rows.map((r) => r.value)), [rows]);
  if (!rows.length) return <p className="muted small">No data yet for this period.</p>;
  return (
    <figure className="bars" aria-label={label}>
      {rows.map((r) => (
        <div key={r.label} className="bar-row">
          <span className="bar-l">{r.label}</span>
          <span className="bar-t"><i style={{ width: `${(r.value / max) * 100}%`, background: r.color ?? color }} /></span>
          <span className="bar-v">{format(r.value)}</span>
        </div>
      ))}
    </figure>
  );
}

/** Vertical bars per day (for counts). */
export function ColumnChart({ days, values, color = '#3563E9', label, format = shortNum }: { days: string[]; values: number[]; color?: string; label: string; format?: (v: number) => string }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = niceMax(Math.max(0, ...values));
  const bw = (W - PL - PR) / Math.max(1, values.length);
  const y = (v: number) => PT + (1 - v / max) * (H - PT - PB);
  const every = Math.max(1, Math.ceil(days.length / 6));
  return (
    <figure className="chart" aria-label={label}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label} onMouseLeave={() => setHover(null)}>
        {[0, 0.5, 1].map((f) => <g key={f}><line x1={PL} x2={W - PR} y1={y(f * max)} y2={y(f * max)} className="ch-grid" /><text x={PL - 6} y={y(f * max) + 4} textAnchor="end" className="ch-ax">{format(f * max)}</text></g>)}
        {values.map((v, i) => (
          <rect key={i} x={PL + i * bw + bw * 0.15} y={y(v)} width={Math.max(1, bw * 0.7)} height={Math.max(0, y(0) - y(v))} rx="2" fill={color} opacity={hover === null || hover === i ? 1 : 0.45} onMouseEnter={() => setHover(i)} />
        ))}
        {days.map((d, i) => (i % every === 0 || i === days.length - 1) && <text key={d} x={PL + i * bw + bw / 2} y={H - 6} textAnchor="middle" className="ch-ax">{dayLabel(d)}</text>)}
      </svg>
      <figcaption className="ch-leg">{hover !== null ? <span className="ch-tip"><b>{dayLabel(days[hover]!)}</b><span><i style={{ background: color }} />{format(values[hover] ?? 0)}</span></span> : <span><i style={{ background: color }} />{label}</span>}</figcaption>
    </figure>
  );
}
